/**
 * @file api.ts
 * @brief TanStack Query hooks for the Company Admin → Analytics feature.
 *
 * @details
 * Company-scoped twin of `features/analytics/api.ts`. Reads the widened
 * `/api/company/analytics/*` endpoints, which force the actual user/company
 * id server-side from the auth token. The client only chooses a *lens* via
 * the `scope` param:
 *   • `personal` → just the signed-in admin's own sessions
 *   • `company`  → the whole company (corporate)
 *
 * Response shapes are identical to the super-admin analytics surface, so we
 * reuse the same Zod schemas from `features/analytics/schemas`.
 *
 * @references
 * - BACKEND/src/features/company/routes.js (analytics endpoints)
 * - features/analytics/schemas (shared response schemas)
 *
 * @author DES
 * @date 09-07-2026
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import {
  LanguageStatsSchema,
  RevenueStatsSchema,
  RevenueTrendSchema,
  SessionStatsSchema,
  SessionTrendSchema,
  SessionsPerUserSchema,
  type AnalyticsRange,
  type GroupBy,
  type LanguageStatRow,
  type RevenueStats,
  type RevenueTrendPoint,
  type SessionStats,
  type SessionTrendPoint,
  type SessionsPerUserRow,
} from "../analytics/schemas";

// ----------------------------------------------------------------------
// Scope — the personal-vs-corporate lens.
// ----------------------------------------------------------------------
export type AnalyticsScope = "personal" | "company";

type ScopedRange = AnalyticsRange & { scope: AnalyticsScope } & Record<
    string,
    unknown
  >;

// ----------------------------------------------------------------------
// Helper — turn `{startDate,endDate,scope,...}` into a query string.
// ----------------------------------------------------------------------
function scopedQuery(range: ScopedRange): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(range)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ----------------------------------------------------------------------
// Query keys — scope is part of every key so switching lens refetches.
// ----------------------------------------------------------------------
export const companyAnalyticsKeys = {
  all: ["company", "analytics"] as const,
  sessionStats: (r: ScopedRange) =>
    ["company", "analytics", "sessions", r] as const,
  sessionTrend: (r: ScopedRange) =>
    ["company", "analytics", "sessions", "trend", r] as const,
  revenueStats: (r: ScopedRange) =>
    ["company", "analytics", "revenue", r] as const,
  revenueTrend: (r: ScopedRange) =>
    ["company", "analytics", "revenue", "trend", r] as const,
  languageStats: (r: ScopedRange) =>
    ["company", "analytics", "languages", r] as const,
  sessionsPerUser: (r: ScopedRange) =>
    ["company", "analytics", "sessions", "per-user", r] as const,
};

// ----------------------------------------------------------------------
// Hooks
// ----------------------------------------------------------------------
export function useCompanySessionStats(
  range: AnalyticsRange & { scope: AnalyticsScope },
  options?: Partial<UseQueryOptions<SessionStats>>,
) {
  return useQuery<SessionStats>({
    queryKey: companyAnalyticsKeys.sessionStats(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsSessions}${scopedQuery(range)}`;
      return SessionStatsSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useCompanySessionTrend(
  range: AnalyticsRange & { scope: AnalyticsScope; groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<SessionTrendPoint[]>>,
) {
  return useQuery<SessionTrendPoint[]>({
    queryKey: companyAnalyticsKeys.sessionTrend(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsSessionsTrend}${scopedQuery(range)}`;
      return SessionTrendSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useCompanyRevenueStats(
  range: AnalyticsRange & { scope: AnalyticsScope; currency?: string },
  options?: Partial<UseQueryOptions<RevenueStats>>,
) {
  return useQuery<RevenueStats>({
    queryKey: companyAnalyticsKeys.revenueStats(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsRevenue}${scopedQuery(range)}`;
      return RevenueStatsSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useCompanyRevenueTrend(
  range: AnalyticsRange & { scope: AnalyticsScope; groupBy?: GroupBy },
  options?: Partial<UseQueryOptions<RevenueTrendPoint[]>>,
) {
  return useQuery<RevenueTrendPoint[]>({
    queryKey: companyAnalyticsKeys.revenueTrend(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsRevenueTrend}${scopedQuery(range)}`;
      return RevenueTrendSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useCompanyLanguageStats(
  range: AnalyticsRange & { scope: AnalyticsScope; limit?: number },
  options?: Partial<UseQueryOptions<LanguageStatRow[]>>,
) {
  return useQuery<LanguageStatRow[]>({
    queryKey: companyAnalyticsKeys.languageStats(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsLanguages}${scopedQuery(range)}`;
      return LanguageStatsSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useCompanySessionsPerUser(
  range: AnalyticsRange & { scope: AnalyticsScope; limit?: number },
  options?: Partial<UseQueryOptions<SessionsPerUserRow[]>>,
) {
  return useQuery<SessionsPerUserRow[]>({
    queryKey: companyAnalyticsKeys.sessionsPerUser(range),
    queryFn: async () => {
      const url = `${companyEndpoints.analyticsSessionsPerUser}${scopedQuery(range)}`;
      return SessionsPerUserSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}
